import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import {
  SignIn, Shield, CreditCard, MagnifyingGlass,
  ArrowsCounterClockwise, Clock, User as UserIcon, CheckCircle,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 19); }
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-[#FF6B00]' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg bg-orange-50 ${color}`}>
            <Icon size={22} weight="fill" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-xl font-bold text-[#111827]">{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminNotificationsPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [logins, setLogins] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmail, setFilterEmail] = useState('');
  const [busyResetting, setBusyResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, sb] = await Promise.all([
        axios.get(`${API}/admin/notifications/summary`, { withCredentials: true }),
        axios.get(`${API}/admin/notifications/logins?limit=100`, { withCredentials: true }),
        axios.get(`${API}/admin/notifications/subscriptions?limit=50`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setLogins(l.data.items || []);
      setSubs(sb.data.items || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Acces administrateur requis');
      } else {
        toast.error('Erreur chargement');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleReset = useCallback(async () => {
    setBusyResetting(true);
    try {
      const res = await axios.post(`${API}/admin/notifications/reset-admin`, {}, { withCredentials: true });
      toast.success(`${res.data.login_events_deleted} connexions admin effacees - testeur et VIP preserves`);
      setConfirmReset(false);
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur reset';
      toast.error(msg);
    } finally { setBusyResetting(false); }
  }, [fetchAll]);

  const filtered = filterEmail
    ? logins.filter(l => (l.email || '').toLowerCase().includes(filterEmail.toLowerCase()))
    : logins;

  if (user?.role !== 'admin') {
    return (
      <AppShell>
        <div className="p-6">
          <Card>
            <CardContent className="p-12 text-center">
              <Shield size={48} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Acces reserve aux administrateurs.</p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6 p-4 sm:p-6" data-testid="admin-notifications-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]">Notifications Admin</h1>
            <p className="text-sm text-gray-500 mt-1">
              Suivi des connexions et des abonnements - <span className="font-semibold">{summary?.admin_email}</span>
            </p>
          </div>
          <Button onClick={() => setConfirmReset(true)} variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="reset-admin-btn">
            <ArrowsCounterClockwise size={18} className="mr-2" />
            Reset compteurs admin
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#FF6B00] mx-auto" />
          </div>
        ) : summary && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-summary">
              <StatCard icon={SignIn} label="Connexions 24h"
                value={summary.logins.last_24h} sub={`${summary.logins.last_7d} sur 7j`} />
              <StatCard icon={UserIcon} label="Utilisateurs uniques 7j"
                value={summary.logins.unique_users_7d} sub={`${summary.logins.total} connexions totales`} />
              <StatCard icon={Shield} label="Connexions admin"
                value={summary.logins.admin_count} sub="Sur votre compte" />
              <StatCard icon={CreditCard} label="Evenements abos"
                value={summary.subscriptions.total_events}
                sub={`${summary.subscriptions.created} actifs, ${summary.subscriptions.canceled} annules`} />
            </div>

            {/* Preserved accounts */}
            <Card data-testid="preserved-accounts">
              <CardContent className="p-4">
                <h3 className="text-sm font-bold text-[#111827] uppercase tracking-wide mb-3">
                  Comptes preserves (non effaces lors du reset)
                </h3>
                <div className="space-y-2">
                  {Object.entries(summary.preserved_accounts).map(([email, info]) => (
                    <div key={email}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      data-testid={`preserved-${email}`}>
                      <span className="text-sm font-medium">{email}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        info.has_connected ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {info.has_connected ? <><CheckCircle size={10} weight="fill" className="inline mr-0.5" /> Connecte ({info.login_count})</> : 'Pas encore connecte'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Logins list */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-[#111827]">
                  Historique des connexions ({filtered.length})
                </h2>
                <div className="relative w-full max-w-xs">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Filtrer par email..."
                    value={filterEmail}
                    onChange={(e) => setFilterEmail(e.target.value)}
                    className="pl-8 h-9 text-sm"
                    data-testid="filter-email"
                  />
                </div>
              </div>
              {filtered.length === 0 ? (
                <Card><CardContent className="p-10 text-center text-sm text-gray-400 italic">
                  Aucune connexion enregistree.
                </CardContent></Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-100" data-testid="logins-list">
                      {filtered.map((evt, idx) => (
                        <div key={`${evt.email}-${evt.created_at}-${idx}`}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50"
                          data-testid={`login-row-${idx}`}>
                          <div className="p-1.5 bg-emerald-50 rounded text-emerald-600">
                            <SignIn size={14} weight="fill" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-[#111827] truncate">
                                {evt.name || evt.email}
                              </span>
                              <span className="text-xs text-gray-500 truncate">{evt.email}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${
                                evt.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {evt.role}
                              </span>
                              {evt.method && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 uppercase">
                                  {evt.method}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                              <Clock size={11} /> {formatDateTime(evt.created_at)}
                              {evt.ip && <span>- IP {evt.ip}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Subscriptions list */}
            {subs.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-[#111827] mb-3">
                  Evenements d&apos;abonnement ({subs.length})
                </h2>
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-100" data-testid="subs-list">
                      {subs.map((s, idx) => (
                        <div key={`sub-${idx}`} className="flex items-center gap-3 p-3"
                          data-testid={`sub-row-${idx}`}>
                          <CreditCard size={16} className="text-[#FF6B00]" weight="fill" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{s.email}</p>
                            <p className="text-xs text-gray-500">
                              {s.action} - {s.plan_id || 'no plan'} - {formatDateTime(s.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset des compteurs admin ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action efface UNIQUEMENT :<br />
              - vos events de connexion (admin@chantix.fr)<br />
              - vos events d&apos;abonnement Stripe<br /><br />
              <span className="font-semibold text-emerald-700">
                Aucun chantier, lot, reserve, inspection ou document ne sera touche.
              </span><br />
              Le compte testeur et le compte VIP Thierry sont preserves.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyResetting} data-testid="reset-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              disabled={busyResetting}
              className="bg-red-500 hover:bg-red-600"
              data-testid="reset-confirm">
              {busyResetting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmer le reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

export default AdminNotificationsPage;
